/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Custom Add Insurance button
 */

import _ from 'lodash';
import { Edit } from '@icon-park/react';
import { useEffect, useMemo, useState } from 'react';
import { Button, Modal, ButtonProps, Alert, App, Tooltip } from 'antd';
import { createForm, onFieldInputValueChange, onFormReset } from '@formily/core';
import { Form } from '@formily/antd-v5';

import SchemaField from 'lib/formily/SchemaField';
import SubmitField from 'lib/formily/SubmitField';
import { NAVIGATION_PROMPT_MESSAGE } from 'components/page';
import getInsuranceSchema, { schemaKeys } from 'components/forms/schema/insuranceSchema';
import usePostCoverage from 'hooks/patients/mutate/usePostCoverage';
import useUpdateCoverage from 'hooks/patients/mutate/useUpdateCoverage';
import appEvents from '@veritable/common/utils/appEvents';
import { getInitialModel, prepareSubmitModel } from 'utils/patients/insurance';
import { PAYER_RESPONSIBILITY_OPTIONS } from 'app-constants/claims/claimRequest';

export interface IProps extends ButtonProps {
	existingPayerResponsibility: string[];
	patientId?: string;
	parsedCoverage?: Insurance.IParsedCoverageInfo;
	fhirCoverage?: Fhir.Coverage;
	tenantPayers: Insurance.ITenantPayerOption[];
	iconOnly?: boolean;
	tooltip?: string;
	title?: string;
	refetchSecondaryPayers?: VoidFunction;
	onInsuranceCreation?: (coverageId: string) => void;
}

/**
 * AddInsurance button
 */
const AddInsurance: React.FC<IProps> = ({
	patientId,
	parsedCoverage,
	existingPayerResponsibility,
	tenantPayers,
	iconOnly,
	tooltip = 'Edit Insurance',
	title,
	refetchSecondaryPayers,
	onInsuranceCreation,
	...restProps
}) => {
	const { modal } = App.useApp();
	const [open, setOpen] = useState(false);
	const form = useMemo(() => createForm(), [open]);
	const { mutate: postCoverage, isPending: isAdding } = usePostCoverage();
	const { mutate: updateCoverage, isPending: isUpdating } = useUpdateCoverage();

	const isLoading = isAdding || isUpdating;
	const isEdit = !_.isEmpty(parsedCoverage);
	const schema = useMemo(() => getInsuranceSchema(tenantPayers, isEdit), [form, tenantPayers, isEdit]);
	const initialModel = useMemo(() => getInitialModel(parsedCoverage), [form, parsedCoverage]);

	useEffect(() => {
		form.setInitialValues(initialModel);
		form.setValues(_.cloneDeep(initialModel));
	}, [initialModel]);

	// handle reset
	const handleReset = () => {
		form?.reset();
	};

	// handle duplicate responsibility alert
	useEffect(() => {
		form.setEffects(() => {
			onFieldInputValueChange([schemaKeys.responsibilitySequence], (field) => {
				if (_.includes(existingPayerResponsibility, field.value)) {
					const payerResponsibility = _.find(PAYER_RESPONSIBILITY_OPTIONS, { value: field.value })?.label;
					form.setFieldState('duplicateResponsibilityAlert', (state) => {
						state.componentProps.children = (
							<Alert
								type='warning'
								closable
								message={`Warning: A ${payerResponsibility} insurance already exists. Adding a new ${payerResponsibility}
								 insurance will remove the '${payerResponsibility}' tag from the current one.`}
								className='mb-4'
								onClose={() =>
									form.setFieldState('duplicateResponsibilityAlert', (newState) => {
										newState.componentProps.children = null;
									})
								}
							/>
						);
					});
				} else {
					form.setFieldState('duplicateResponsibilityAlert', (state) => {
						state.componentProps.children = null;
					});
				}
			});
			onFormReset(() => {
				form.setFieldState('duplicateResponsibilityAlert', (state) => {
					state.componentProps.children = null;
				});
			});
		});
	}, [form, existingPayerResponsibility]);

	// handle cancel
	const handleCancel = () => {
		if (_.isEqual(form.values, initialModel)) {
			setOpen(false);
		} else {
			modal.confirm({
				closable: true,
				title: 'Discard Changes?',
				content: NAVIGATION_PROMPT_MESSAGE,
				okText: 'Yes, discard',
				onOk: () => {
					setOpen(false);
				},
			});
		}
	};

	// handle submit
	const handleSubmit = (values: Insurance.IFormModel) => {
		const submitModel = prepareSubmitModel({ model: values, patientId, olderCoverage: parsedCoverage?.fhirCoverage });
		if (!_.isEmpty(parsedCoverage)) {
			updateCoverage(
				{
					coverageId: parsedCoverage?.coverageId,
					coveragePatchModel: { fhirResource: submitModel },
				},
				{
					onSuccess: () => {
						appEvents.publishApiSuccess('Insurance updated successfully');
						setOpen(false);
					},
				},
			);
		} else {
			postCoverage(
				{ patientId, coveragePostModel: { fhirResource: submitModel } },
				{
					onSuccess: (newCoverageId: string) => {
						appEvents.publishApiSuccess('Insurance added successfully');
						setOpen(false);
						refetchSecondaryPayers?.();
						onInsuranceCreation?.(newCoverageId);
					},
				},
			);
		}
	};

	const button = useMemo(() => {
		if (iconOnly) {
			return (
				<Tooltip title={tooltip}>
					<Button color='default' icon={<Edit />} onClick={() => setOpen(true)} {...restProps} />
				</Tooltip>
			);
		}
		return (
			<Button type={restProps.type || 'primary'} onClick={() => setOpen(true)} {...restProps}>
				{title ?? 'Add Insurance'}
			</Button>
		);
	}, [iconOnly, restProps]);

	return (
		<>
			{button}
			<Modal
				open={open}
				destroyOnHidden
				title={isEdit ? 'Edit Insurance' : 'Add Insurance'}
				closable={true}
				onCancel={handleCancel}
				footer={null}
				width={'42%'}
				className='tw-modal-form'
				maskClosable={false}
				okButtonProps={{ disabled: isLoading, loading: isLoading }}
				cancelButtonProps={{ disabled: isLoading }}
				zIndex={1050}
			>
				<Form form={form} layout='vertical' key={form.id}>
					<SchemaField schema={schema} />
					<div className='tw-modal-form-footer'>
						<Button key='reset' onClick={handleReset} disabled={isLoading}>
							{'Reset'}
						</Button>
						<SubmitField
							key={form.id}
							initialModel={initialModel}
							disabled={isLoading}
							loading={isLoading}
							onSubmit={handleSubmit}
						/>
					</div>
				</Form>
			</Modal>
		</>
	);
};

export default AddInsurance;
